import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";

function StrategyManager() {
  const [strategies, setStrategies] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [formData, setFormData] = useState(initialFormData());
  const [jsonText, setJsonText] = useState("{}");
  const [jsonError, setJsonError] = useState(null);
  const [editStrategy, setEditStrategy] = useState(null);
  const baseUrl = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    fetchStrategies();
    fetchWatchlists();
    setJsonText(JSON.stringify(initialFormData().condition_json, null, 2));
  }, []);

  function initialFormData() {
    return {
      user_id: 1,
      name: "",
      action: "BUY",
      watchlist_id: "",
      condition_json: { action: "BUY", stok_status: "P" },
      active: true
    };
  }

  const fetchStrategies = async () => {
    const res = await axios.get(`${baseUrl}/api/strategies/`);
    setStrategies(res.data);
  };

  const fetchWatchlists = async () => {
    const res = await axios.get(`${baseUrl}/get_watchlists`);
    setWatchlists(res.data);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonText(value);
    try {
      const parsed = JSON.parse(value);
      setFormData({ ...formData, condition_json: parsed });
      setJsonError(null);
    } catch {
      setJsonError("Invalid JSON format");
    }
  };

  const beautifyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const pretty = JSON.stringify(parsed, null, 2);
      setJsonText(pretty);
      setJsonError(null);
    } catch {
      setJsonError("Cannot beautify: Invalid JSON");
    }
  };

  const applyTemplate = (template) => {
    let templateJson = {};
    if (template === "buy") {
      templateJson = {
        action: "BUY",
        stok_status: "P",
        symbol_type: "EQ",
        ltp_between: [100, 2000],
        ecount_lt: 10,
        watchlist_id: 1,
        allocation_amount: 50000
      };
    } else if (template === "sell") {
      templateJson = {
        action: "SELL",
        stok_status: "E",
        watchlist_id: 1,
        conditions: {
          gain_percent_gte: 5,
          loss_percent_lte: -5,
          age_gte: 15
        }
      };
    } else if (template === "portfolio") {
      templateJson = {
        action: "SELL",
        stok_status: "E",
        scope: "portfolio",
        conditions: {
          gain_percent_gte: 3,
          loss_percent_lte: -25
        }
      };
    }
    setJsonText(JSON.stringify(templateJson, null, 2));
    setFormData({ ...formData, condition_json: templateJson });
    setJsonError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (jsonError) return;
    await axios.post(`${baseUrl}/api/strategies/`, formData);
    fetchStrategies();
    setFormData(initialFormData());
    setJsonText(JSON.stringify(initialFormData().condition_json, null, 2));
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this strategy?")) {
      await axios.delete(`${baseUrl}/api/strategies/${id}`);
      fetchStrategies();
    }
  };

  const handleEdit = (strategy) => {
    setEditStrategy(strategy);
    setFormData(strategy);
    setJsonText(JSON.stringify(strategy.condition_json, null, 2));
  };

  const handleUpdate = async () => {
    if (jsonError) return;
    await axios.put(`${baseUrl}/api/strategies/${editStrategy.id}`, formData);
    fetchStrategies();
    setEditStrategy(null);
    setFormData(initialFormData());
    setJsonText(JSON.stringify(initialFormData().condition_json, null, 2));
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4 text-center">📊 Strategy Manager</h2>

      {/* Strategy Form */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Strategy Name</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Action</label>
              <select
                className="form-select"
                name="action"
                value={formData.action}
                onChange={handleChange}
              >
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Watchlist</label>
              <select
                className="form-select"
                name="watchlist_id"
                value={formData.watchlist_id}
                onChange={handleChange}
              >
                <option value="">Select Watchlist</option>
                {watchlists.map((wl) => (
                  <option key={wl.id} value={wl.id}>
                    {wl.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">Condition JSON</label>
                <div>
                  <select
                    className="form-select form-select-sm d-inline-block w-auto me-2"
                    onChange={(e) => applyTemplate(e.target.value)}
                  >
                    <option value="">Insert Template...</option>
                    <option value="buy">BUY Template</option>
                    <option value="sell">SELL Template</option>
                    <option value="portfolio">Portfolio SELL Template</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={beautifyJson}
                  >
                    Beautify JSON
                  </button>
                </div>
              </div>
              <textarea
                className={`form-control ${jsonError ? "is-invalid" : ""}`}
                rows="8"
                value={jsonText}
                onChange={handleJsonChange}
              />
              {jsonError && (
                <div className="invalid-feedback">{jsonError}</div>
              )}
            </div>

            <div className="col-12 form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              <label className="form-check-label">Active</label>
            </div>

            <div className="col-12 text-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!!jsonError}
              >
                Save Strategy
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Existing Strategies */}
      <h3 className="mb-3">Existing Strategies</h3>
      <div className="row">
        {strategies.map((s) => (
          <div key={s.id} className="col-md-6 mb-3">
            <div className="card shadow-sm h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">
                  {s.name}{" "}
                  <span className="badge bg-secondary">{s.action}</span>
                </h5>
                <pre className="bg-light p-2 rounded small flex-grow-1">
                  {JSON.stringify(s.condition_json, null, 2)}
                </pre>
                <div className="mt-3 d-flex justify-content-between">
                  <span
                    className={`badge ${
                      s.active ? "bg-success" : "bg-danger"
                    }`}
                  >
                    {s.active ? "Active" : "Inactive"}
                  </span>
                  <div>
                    <button
                      className="btn btn-sm btn-warning me-2"
                      onClick={() => handleEdit(s)}
                      data-bs-toggle="modal"
                      data-bs-target="#editModal"
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(s.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editStrategy && (
        <div
          className="modal fade show"
          id="editModal"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Strategy</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditStrategy(null)}
                ></button>
              </div>
              <div className="modal-body">
                <label className="form-label">Strategy Name</label>
                <input
                  type="text"
                  className="form-control mb-3"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />

                <label className="form-label">Condition JSON</label>
                <textarea
                  className={`form-control ${jsonError ? "is-invalid" : ""}`}
                  rows="8"
                  value={jsonText}
                  onChange={handleJsonChange}
                />
                {jsonError && (
                  <div className="invalid-feedback">{jsonError}</div>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={beautifyJson}
                >
                  Beautify JSON
                </button>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setEditStrategy(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdate}
                  disabled={!!jsonError}
                >
                  Update Strategy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategyManager;
